import React, { useState, useEffect } from "react";
//import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faEnvelope,
  faGraduationCap,
  faHandshake,
  faAnchor,
  faPaperPlane,
  faCheckCircle,
  faFileAlt,
  faStore,
  faUtensils,
  faShip,
  faWrench,
  faHotel,
  faUserTie,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { CinematicHero, GlassSection, R5Tabs, R5Faq, R5HelpCard } from "../components/ui";
import "../styles/HelpSupport.css";

// Dummy FAQ data
const FAQ_CATEGORIES = [
  {
    id: "general",
    name: "General",
    icon: faQuestionCircle,
    questions: [
      {
        id: "faq-1",
        question: "What is the Pirate's Guild?",
        answer:
          "The USVI Pirate's Guild is a free travel perks, local discovery, and business promotion network for the U.S. Virgin Islands. Travelers join free to find local deals, maps, events, and member perks. Businesses join free to be discovered by travelers, with optional paid upgrades for premium visibility, promotion, analytics, and bookings.",
      },
      {
        id: "faq-2",
        question: "How do I become a member?",
        answer:
          "Sign up free as a Traveler (Deckhand pass) or as a Business (Crew Listing). You can upgrade later to a paid Traveler pass for premium perks, or to a Featured Captain or Port Sponsor business package for promotion and analytics.",
      },
      {
        id: "faq-3",
        question: "What are Doubloons and how do they work?",
        answer:
          "Doubloons are the Pirate's Guild loyalty rewards. You earn them as you explore the islands, visit participating businesses, attend events, complete treasure hunts, and refer friends. Doubloons are NOT required to access standard Guild deals — they're bonus rewards you can redeem for member-only perks, event upgrades, sponsored partner perks, and limited-time fun extras.",
      },
      {
        id: "faq-4",
        question: "How long is my membership valid?",
        answer:
          "Memberships are available in monthly or annual subscriptions. You can view and manage your subscription details in your account settings.",
      },
    ],
  },
  {
    id: "travelers",
    name: "For Travelers",
    icon: faShip,
    questions: [
      {
        id: "faq-5",
        question: "How do I find participating businesses?",
        answer:
          "All participating businesses are listed in the Business Listings section. You can browse by category, location, or search for specific businesses.",
      },
      {
        id: "faq-6",
        question: "Can I purchase Doubloons before arriving on the island?",
        answer:
          "Yes — if you want to top up your Doubloon balance for bonus rewards, you can buy Doubloon packs from your account at any time. Remember: Doubloons are not required to access standard Guild deals; they're loyalty rewards for extra perks.",
      },
      {
        id: "faq-7",
        question: "How do I use the Roommate Finder feature?",
        answer:
          "The Roommate Finder is available to Traveler tier members. After passing a simple background check, you can browse listings for short-term and long-term accommodations posted by verified members.",
      },
    ],
  },
  {
    id: "businesses",
    name: "For Businesses",
    icon: faStore,
    questions: [
      {
        id: "faq-8",
        question: "How do I join as a business partner?",
        answer:
          "Sign up free for a Crew Listing to be listed in our directory. From there you can offer Guild deals, participate in events, and connect with travelers. Upgrade to Featured Captain or Port Sponsor when you want premium placement, promotion, analytics, sponsored campaigns, or booking support.",
      },
      {
        id: "faq-9",
        question: "What are the benefits of offering Guild deals?",
        answer:
          "Offering a Guild deal increases your visibility to Guild travelers and members. You can create unique offers that drive traffic during slow periods, engage with the community, and reward loyal customers with sponsored Doubloon rewards.",
      },
      {
        id: "faq-10",
        question: "How do I set up Guild deals and Doubloon offers?",
        answer:
          "In your Business Dashboard, open the Doubloon Offers and Redeemables sections to create and manage Guild deals, redemption codes, sponsored Doubloon rewards, and event promotions.",
      },
    ],
  },
  {
    id: "technical",
    name: "Technical Support",
    icon: faWrench,
    questions: [
      {
        id: "faq-11",
        question: "I forgot my password. How do I reset it?",
        answer:
          'You can reset your password by clicking the "Forgot Password" link on the login page. We\'ll send a password reset link to your registered email address.',
      },
      {
        id: "faq-12",
        question: "How do I update my payment information?",
        answer:
          "Go to Account Settings > Subscriptions > Payment Methods to update your payment information.",
      },
    ],
  },
];

// Dummy training courses data
const TRAINING_COURSES = [
  {
    id: "course-1",
    title: "Food Handler Certification",
    icon: faUtensils,
    description:
      "USVI-approved food safety certification for restaurant staff.",
    level: "Beginner",
    duration: "4 hours",
    price: 49.99,
    type: "certification",
  },
  {
    id: "course-2",
    title: "Hospitality Basics",
    icon: faHotel,
    description:
      "Introduction to hospitality service standards for new industry employees.",
    level: "Beginner",
    duration: "2 hours",
    price: 0,
    type: "intro",
  },
  {
    id: "course-3",
    title: "Advanced Customer Service",
    icon: faUserTie,
    description:
      "Elevate your customer service skills to create memorable guest experiences.",
    level: "Intermediate",
    duration: "6 hours",
    price: 79.99,
    type: "advanced",
  },
  {
    id: "course-4",
    title: "Island Tour Guide Training",
    icon: faShip,
    description:
      "Learn to conduct engaging and informative tours around the USVI.",
    level: "Intermediate",
    duration: "8 hours",
    price: 99.99,
    type: "certification",
  },
  {
    id: "course-5",
    title: "Bartender Certification",
    icon: faUtensils,
    description: "Comprehensive bartending and responsible service training.",
    level: "Intermediate",
    duration: "12 hours",
    price: 129.99,
    type: "certification",
  },
];

// Dummy consulting services
const CONSULTING_SERVICES = [
  {
    id: "consult-1",
    title: "Business Setup Consultation",
    icon: faHandshake,
    description:
      "Expert guidance on establishing or expanding your business in the USVI.",
    duration: "2 hours",
    price: 199.99,
  },
  {
    id: "consult-2",
    title: "Marketing Strategy Session",
    icon: faFileAlt,
    description:
      "Develop targeted marketing strategies to attract tourists and locals.",
    duration: "1 hour",
    price: 149.99,
  },
  {
    id: "consult-3",
    title: "Operational Efficiency Review",
    icon: faWrench,
    description:
      "Identify opportunities to streamline operations and reduce costs.",
    duration: "3 hours",
    price: 249.99,
  },
  {
    id: "consult-4",
    title: "Seasonal Planning Workshop",
    icon: faCalendarAlt,
    description:
      "Prepare your business for high and low seasons with strategic planning.",
    duration: "2 hours",
    price: 199.99,
  },
];

function HelpSupport() {
  //const { isAuthenticated } = useAuth();
  const { isTraveler, isBusinessOwner } = usePlans();
  const [activeTab, setActiveTab] = useState("faq");
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Check subscription types (resolved from the member's synced Stripe tier).
  const checkSubscriptionType = () => ({
    traveler: typeof isTraveler === "function" ? isTraveler() : false,
    business: typeof isBusinessOwner === "function" ? isBusinessOwner() : false,
  });

  const { business } = checkSubscriptionType();

  // Check for consulting hours available
  const hasConsultingHours = business; // In a real app, this would check the specific business plan details

  // Simulate loading data
  useEffect(() => {
    setTimeout(() => {
      setFilteredFaqs(FAQ_CATEGORIES);
      setLoading(false);
    }, 1000);
  }, []);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      setFilteredFaqs(FAQ_CATEGORIES);
      return;
    }

    const lowerQuery = query.toLowerCase();

    // Filter categories and questions
    const filtered = FAQ_CATEGORIES.map((category) => {
      const matchingQuestions = category.questions.filter(
        (q) =>
          q.question.toLowerCase().includes(lowerQuery) ||
          q.answer.toLowerCase().includes(lowerQuery)
      );

      if (matchingQuestions.length > 0) {
        return {
          ...category,
          questions: matchingQuestions,
        };
      }

      return null;
    }).filter(Boolean);

    setFilteredFaqs(filtered);
  };

  // Handle contact form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle contact form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();
    // In a real app, this would send the form data to your backend
    console.log("Form submitted:", contactForm);

    // Simulate form submission
    setTimeout(() => {
      setFormSubmitted(true);
      setContactForm({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    }, 1000);
  };

  // Render FAQ section — R5-M1 categorized accordion using native <details>.
  const renderFaq = () => {
    if (filteredFaqs.length === 0) {
      return (
        <div className="no-results">
          <p>No FAQs match your search query. Try different keywords or browse our categories.</p>
        </div>
      );
    }

    const categories = filteredFaqs.map((cat) => ({
      label: cat.name,
      items: cat.questions.map((q) => ({ q: q.question, a: q.answer })),
    }));

    return (
      <>
        <div className="r5-help-search">
          <input
            type="search"
            placeholder="Search the FAQ…"
            value={searchQuery}
            onChange={handleSearch}
            aria-label="Search FAQ"
          />
        </div>
        <R5Faq categories={categories} />
      </>
    );
  };

  // Render contact form
  const renderContactForm = () => {
    if (formSubmitted) {
      return (
        <div className="form-submitted">
          <div className="success-icon">
            <FontAwesomeIcon icon={faCheckCircle} size="3x" />
          </div>
          <h3>Thank You!</h3>
          <p>
            Your message has been submitted. We'll get back to you as soon as
            possible.
          </p>
          <button
            className="new-message-btn"
            onClick={() => setFormSubmitted(false)}
          >
            Send Another Message
          </button>
        </div>
      );
    }

    return (
      <div className="contact-form-section">
        <h2>Contact Support</h2>
        <p className="section-description">
          Have a question that's not answered in our FAQ? Send us a message and
          we'll get back to you.
        </p>

        <form className="contact-form" onSubmit={handleFormSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Your Name</label>
              <input
                type="text"
                name="name"
                value={contactForm.name}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={contactForm.email}
                onChange={handleFormChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Subject</label>
            <input
              type="text"
              name="subject"
              value={contactForm.subject}
              onChange={handleFormChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Message</label>
            <textarea
              rows="5"
              name="message"
              value={contactForm.message}
              onChange={handleFormChange}
              required
            ></textarea>
          </div>

          <button type="submit" className="submit-btn">
            <FontAwesomeIcon icon={faPaperPlane} />
            <span>Send Message</span>
          </button>
        </form>
      </div>
    );
  };

  // Render training courses — R5-M1 help-pair grid with R5HelpCard.
  const renderTrainingCourses = () => {
    return (
      <div className="r5-help-pair">
        {TRAINING_COURSES.map((course) => (
          <R5HelpCard
            key={course.id}
            title={course.title}
            description={course.description}
            tags={[
              course.level,
              course.duration,
              course.price === 0 ? "Free" : `$${course.price}`,
            ].filter(Boolean)}
            action={
              <button type="button" className="r5-pack-cta r5-pack-cta--bright" style={{ marginTop: 10 }}>
                {course.price === 0 ? "Start Free Course" : "Enroll Now"}
              </button>
            }
          />
        ))}
      </div>
    );
  };

  // Render consulting services — R5-M1 help-pair grid with R5HelpCard.
  const renderConsultingServices = () => {
    if (!business) {
      return (
        <div className="business-upgrade-needed">
          <h2>Consulting Services</h2>
          <p>You need a business subscription to access our consulting services.</p>
          <button className="upgrade-btn r5-pack-cta r5-pack-cta--bright">Upgrade to Business</button>
        </div>
      );
    }

    return (
      <>
        {hasConsultingHours ? (
          <div className="consulting-hours-available" style={{ marginBottom: 16 }}>
            <p>
              <strong>Available consulting hours:</strong> 5 hours
            </p>
            <button className="r5-pack-cta r5-pack-cta--bright">Schedule a Consultation</button>
          </div>
        ) : (
          <div className="consulting-hours-empty" style={{ marginBottom: 16 }}>
            <p>
              You currently have no consulting hours available. Purchase a
              consulting package to get started.
            </p>
            <button className="r5-pack-cta">Purchase Consulting Hours</button>
          </div>
        )}

        <div className="r5-help-pair">
          {CONSULTING_SERVICES.map((service) => (
            <R5HelpCard
              key={service.id}
              title={service.title}
              description={service.description}
              tags={[service.duration, `$${service.price}`].filter(Boolean)}
              action={
                <button type="button" className="r5-pack-cta" style={{ marginTop: 10 }}>
                  Book Consultation
                </button>
              }
            />
          ))}
        </div>
      </>
    );
  };

  // Main render
  const helpTabs = [
    { key: "faq", label: "FAQ", icon: <FontAwesomeIcon icon={faQuestionCircle} /> },
    { key: "contact", label: "Contact Support", icon: <FontAwesomeIcon icon={faEnvelope} /> },
    { key: "training", label: "Training & Certification", icon: <FontAwesomeIcon icon={faGraduationCap} /> },
    { key: "consulting", label: "Consulting Services", icon: <FontAwesomeIcon icon={faHandshake} /> },
  ];

  return (
    <>
      <CinematicHero
        eyebrow="· Help & Support ·"
        title="The Lookout"
        dropCap="T"
        lede="Browse FAQs, hail the crew, or learn the ropes — the watch is always on."
      />
      <R5Tabs
        tabs={helpTabs}
        activeKey={activeTab}
        onChange={setActiveTab}
      />
      <GlassSection className="help-support-page">
      <div className="page-content">
        {loading ? (
          <div className="loading-spinner">
            <FontAwesomeIcon icon={faAnchor} spin />
            <p>Loading help center...</p>
          </div>
        ) : (
          <>
            {activeTab === "faq" && renderFaq()}
            {activeTab === "contact" && renderContactForm()}
            {activeTab === "training" && renderTrainingCourses()}
            {activeTab === "consulting" && renderConsultingServices()}
          </>
        )}
      </div>
      </GlassSection>
    </>
  );
}

export default HelpSupport;
